/**
 * As mensagens que a venda manda ao cliente (F245/F246) — as regras puras.
 *
 * - **F245:** "seu pedido saiu com o motoboy", quando o caixa marca a saída.
 * - **F246:** "deu certo a instalação?", horas depois da compra, só em horário
 *   de loja. A resposta decide o resto: "deu certo" recebe a oferta; "não
 *   deu" marca a conversa para a vendedora, sem resposta automática.
 *
 * Aqui não tem banco nem relógio: o horário, o texto e a leitura da resposta.
 */
import { normalizeText } from "./price-reply.js";
/** O horário da Elite: seg–sex 8h–18h, sáb 8h–12h, domingo fechado. */
export const DEFAULT_AFTERSALE_HOURS = {
    timezone: 'America/Sao_Paulo',
    windows: [
        ...[1, 2, 3, 4, 5].map((day) => ({ day, start: '08:00', end: '18:00' })),
        { day: 6, start: '08:00', end: '12:00' },
    ],
};
/** O que uma loja sem configuração vê na tela — tudo desligado. */
export const DEFAULT_SALE_MESSAGE_CONFIG = {
    dispatchedEnabled: false,
    dispatchedText: 'Oi, {cliente}! Seu pedido saiu agora com o motoboy 🛵 e já está a caminho.',
    aftersaleEnabled: false,
    aftersaleDelayHours: 4,
    aftersaleHours: DEFAULT_AFTERSALE_HOURS,
    aftersaleCheckText: 'Oi, {cliente}! Aqui é {vendedora}, da loja 😊 Deu tudo certo com a instalação da {peca}?',
    aftersaleOfferText: 'Que bom! 🙌 Precisa de mais alguma peça ou acessório? Estamos com caixa de som em promoção 🔊',
};
const WEEKDAY = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
const minutesOf = (clock) => {
    const [hour, minute] = clock.split(':').map(Number);
    return (hour ?? 0) * 60 + (minute ?? 0);
};
/** O dia da semana e o minuto do dia de `at`, no fuso do horário. */
const localClock = (timezone, at) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(at);
    const part = (type) => parts.find((entry) => entry.type === type)?.value ?? '0';
    return {
        day: WEEKDAY[part('weekday')] ?? 0,
        minute: (Number(part('hour')) % 24) * 60 + Number(part('minute')),
        second: Number(part('second')),
    };
};
/**
 * O primeiro instante, a partir de `at`, em que a loja está aberta: o próprio
 * `at` se já estiver; senão, o começo da próxima janela. A venda das 16h de
 * sexta, com 4h de espera (20h), pergunta no sábado às 8h. `null` quando o
 * horário não tem janela nenhuma.
 *
 * O fuso conta como fixo ao longo do dia — o Brasil não tem horário de verão
 * desde 2019.
 */
export const nextOpenInstant = (hours, at) => {
    if (hours.windows.length === 0)
        return null;
    const now = localClock(hours.timezone, at);
    // A meia-noite local de hoje, em UTC.
    const midnight = at.getTime() - (now.minute * 60 + now.second) * 1000 - at.getMilliseconds();
    for (let offset = 0; offset <= 7; offset += 1) {
        const day = (now.day + offset) % 7;
        const windows = hours.windows
            .filter((window) => window.day === day)
            .map((window) => ({
            start: minutesOf(window.start),
            end: minutesOf(window.end),
        }))
            .sort((left, right) => left.start - right.start);
        for (const window of windows) {
            if (offset === 0) {
                if (now.minute >= window.start && now.minute < window.end)
                    return at;
                if (now.minute >= window.end)
                    continue;
            }
            return new Date(midnight + (offset * 1440 + window.start) * 60_000);
        }
    }
    return null;
};
/** Quando sai o "deu certo?": `delay` horas depois, empurrado para a loja aberta. */
export const aftersaleDueAt = (config, at) => {
    const later = new Date(at.getTime() + config.aftersaleDelayHours * 3_600_000);
    return nextOpenInstant(config.aftersaleHours, later) ?? later;
};
/** O primeiro nome, com a inicial maiúscula: "JOAO DA SILVA" → "Joao". */
export const firstName = (name) => {
    const first = name?.trim().split(/\s+/)[0];
    if (first === undefined || first === '')
        return null;
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};
/**
 * O texto com `{cliente}`, `{vendedora}` e `{peca}` trocados. Sem o valor, a
 * variável some com a vírgula ou o espaço da frente: "Oi, {cliente}!" vira
 * "Oi!", e não "Oi, !".
 */
export const renderSaleMessage = (text, variables) => {
    const fill = (key) => {
        const value = variables[key]?.trim() ?? '';
        return value === '' ? null : value;
    };
    return text
        .replace(/([,\s]*)\{(cliente|vendedora|peca)\}/g, (_match, before, key) => {
        const value = fill(key);
        if (value === null)
            return before.includes(',') ? '' : before;
        return `${before}${value}`;
    })
        .replace(/\s+([!?.,])/g, '$1')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
};
const PROBLEM = [
    /\bn(ao|n)\b[^.!?]*\b(deu|funcion\w*|pegou|liga\w*|carreg\w*|serviu|ficou|encaix\w*|acend\w*|reconhec\w*|presta\w*)/,
    /\bproblema/,
    /\bdefeit/,
    /\bquebr/,
    /\btravand/,
    /\bfantasma/,
    /\bsem (imagem|touch|toque|carga|som)/,
    /\bpiscand/,
    /\bmanchad/,
    /\bdesligand/,
    /\bnao (esta|ta) (funcionando|pegando|ligando|carregando)/,
];
const NOT_YET = /\b(ainda nao|nao instal|vou instalar|amanha|depois eu|mais tarde)/;
const WORKED = [
    /\b(sim|deu certo|deu sim|deu bom|funcionou|funcionando|perfeit\w*|tudo certo|tudo ok|certinho|beleza|blz|show|top|otimo|excelente|ficou (bom|top|show|otimo|perfeito)|ok|okay|tranquilo|de boa|joia)\b/,
];
const WORKED_EMOJI = /[👍✅👌🙏🤝💯😁😃😊]/u;
/**
 * A resposta ao "deu certo a instalação?":
 *
 * - `problem` vem antes: "sim, mas tá com toque fantasma" é problema;
 * - "não" sozinho é problema; "ainda não instalei" é outra coisa;
 * - `worked` é o "sim", "deu certo", "funcionou", 👍;
 * - o resto (uma pergunta de preço, um áudio sem texto) é `other`, e quem
 *   responde é a vendedora.
 */
export const classifyAftersaleReply = (text) => {
    if (text === null)
        return 'other';
    const normalized = normalizeText(text).replace(/\s+/g, ' ').trim();
    if (NOT_YET.test(normalized))
        return 'other';
    if (PROBLEM.some((pattern) => pattern.test(normalized)))
        return 'problem';
    if (/^(nao|n|nn|nada|negativo)[.!]*$/.test(normalized))
        return 'problem';
    if (WORKED.some((pattern) => pattern.test(normalized)))
        return 'worked';
    if (WORKED_EMOJI.test(text))
        return 'worked';
    return 'other';
};
//# sourceMappingURL=sale-messages.js.map